import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * What a partner sees about their own franchises — a **third principal**, reading three endpoints of
 * its own and none of the super-admin's.
 *
 * `/partner/me/franchises` lists the franchises they hold a share in; `/partner/me/profitability`
 * totals what was billed and what their share of it comes to; `/partner/me/invoices` is their own
 * invoices. All three are scoped server-side to the partner in the token — nothing here passes an id.
 *
 * **The payout can be negative, and it is not a payout then.** `PartnerPortalService` computes
 * `amount * share / 100d` and the same figure appears on the tower's Profitability screen, where legacy
 * draws every one of them green (FB-S02). Measured there: a franchise with no revenue and 30,46 € of
 * cost charges its partners −21,32 € and −9,14 €. The rule from that finding is applied here too,
 * because it is the same number seen by the person it is taken from.
 *
 * **The figures are read, never recomputed.** The share arithmetic is `double` server-side; multiplying
 * it again here in floating point would produce a second, differently-wrong answer for the same row.
 */

export interface PartnerFranchise {
  franchiseId: number
  franchiseName: string
  franchiseSlug: string
  isActive: boolean
  sharePct: number
  /**
   * The package this franchise is on - §3.122. `null` where it is on none.
   *
   * **The server has always answered it and this interface never declared it.**
   * `PartnerPortalService.franchises` runs legacy's two-step and puts `packageName` on every row; its
   * javadoc's first sentence says so. Same shape as §3.97's `deliveryDate` and §3.121's `notes` - a
   * field on the wire that no census reading the interface could see was missing.
   */
  packageName: string | null
}

export interface PartnerPayout {
  franchiseId: number
  franchiseName: string
  revenueCents: number
  /** §3.68 - what the platform spent to earn that revenue, pro-rated across the window. */
  costCents: number
  /** §3.68 - revenue less costs. **This is what the share is taken from**, per the owner. */
  profitCents: number
  payoutCents: number
  sharePct: number
}

export interface PartnerProfitability {
  from: string
  to: string
  franchises: PartnerPayout[]
  totalRevenueCents: number
  totalCostCents: number
  totalProfitCents: number
  totalPayoutCents: number
}

function num(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function partnerFranchisesOf(payload: unknown): PartnerFranchise[] {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const rows = Array.isArray(row.franchises) ? row.franchises : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const link = entry as Record<string, unknown>
    return [
      {
        franchiseId: num(link.franchiseId),
        franchiseName: str(link.franchiseName),
        franchiseSlug: str(link.franchiseSlug),
        /* §3.122 - **`active`, which is the key the server sends.** It read `link.isActive`, and
           `undefined !== false` is true, so every franchise the portal ever drew was active and
           `inactiveNote` never fired once. The server puts `active` (and legacy sends `active:
           r.isActive` too); `PartnerPortalFieldsAgreeTest` holds the two sides together now.

           Still defaulting to active for an absent value: a franchise whose flag did not arrive is not
           evidence it is switched off, and greying a live franchise's row would be the worse mistake. */
        isActive: link.active !== false,
        sharePct: num(link.sharePct),
        // §3.122 - `null` where the franchise is on no package, which `LEFT JOIN packages` produces.
        packageName: typeof link.packageName === 'string' && link.packageName !== '' ? link.packageName : null,
      },
    ]
  })
}

/**
 * The period the payout figure covers - FB-824, §3.153.
 *
 * **The server refuses a request without both bounds.** `PartnerPortalService.profitability` throws
 * `badRequest("from + to required (YYYY-MM-DD)")`, and the screen sent neither, so it answered 400 for as long as
 * the page has existed and *Váš podiel za obdobie* rendered `0,00 €`.
 *
 * Year to date, which is legacy's own default (`PartnerPortal.tsx:176` is `startOfYear` and `today`): a partner's
 * first question is what they have earned this year. Ported rather than invented, and a blank range would put the
 * 400 back.
 */
export function yearToDate(now: Date): { from: string; to: string } {
  return { from: `${now.getFullYear()}-01-01`, to: isoDay(now) }
}

/** This month so far - legacy's second quick range, *Tento mesiac*. */
export function monthToDate(now: Date): { from: string; to: string } {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return { from: `${now.getFullYear()}-${month}-01`, to: isoDay(now) }
}

/**
 * The whole of last month - legacy's third quick range, *Minulý mesiac* - FB-960, §3.186.
 *
 * **The range the figure is actually about.** `BillingRunner` bills a **completed** month, so a partner asking
 * what they are owed for September wants exactly that month; `yearToDate` and `monthToDate` both end today,
 * inside a month that is not finished, and on the first of a month the second is a single day.
 *
 * **Legacy's own arithmetic, ported rather than rewritten** (`PartnerPortal.tsx:222`): `new Date(y, m - 1, 1)`
 * and `new Date(y, m, 0)`, where **day 0 of this month is the last day of the previous one** - so the end of the
 * range needs no knowledge of month lengths or of leap years.
 *
 * **January is why that matters.** `getMonth() - 1` is `-1`, which `Date` rolls back into December of the
 * previous year, so a partner opening the portal in January reads `2025-12-01` to `2025-12-31`. Building the
 * string by hand would have produced a month `00`.
 */
export function lastMonth(now: Date): { from: string; to: string } {
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const last = new Date(now.getFullYear(), now.getMonth(), 0)
  return { from: isoDay(first), to: isoDay(last) }
}

/**
 * `YYYY-MM-DD` in the viewer's own day, not UTC.
 *
 * `toISOString()` is the reflex and it is wrong here: at 01:00 in Bratislava it answers **yesterday**, so a partner
 * opening the portal after midnight would ask for a range ending the day before and read a figure short by a day.
 */
export function isoDay(when: Date): string {
  const month = String(when.getMonth() + 1).padStart(2, '0')
  const day = String(when.getDate()).padStart(2, '0')
  return `${when.getFullYear()}-${month}-${day}`
}

/** What the card says the figure covers, from the server's own echo rather than the inputs. */
export function periodNote(report: { from: string; to: string }): string {
  if (report.from === '' || report.to === '') return ''
  return `${report.from} - ${report.to}`
}

export function partnerProfitabilityOf(payload: unknown): PartnerProfitability {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const totals = (typeof row.totals === 'object' && row.totals !== null ? row.totals : {}) as Record<string, unknown>
  const rows = Array.isArray(row.franchises) ? row.franchises : []
  return {
    from: str(row.from),
    to: str(row.to),
    franchises: rows.flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return []
      const item = entry as Record<string, unknown>
      return [
        {
          franchiseId: num(item.franchiseId),
          franchiseName: str(item.franchiseName),
          revenueCents: parseMoneyToCents(item.revenue as string | number | null),
          costCents: parseMoneyToCents(item.costs as string | number | null),
          profitCents: parseMoneyToCents(item.profit as string | number | null),
          payoutCents: parseMoneyToCents(item.payout as string | number | null),
          sharePct: num(item.sharePct),
        },
      ]
    }),
    totalRevenueCents: parseMoneyToCents(totals.revenue as string | number | null),
    totalCostCents: parseMoneyToCents(totals.costs as string | number | null),
    totalProfitCents: parseMoneyToCents(totals.profit as string | number | null),
    totalPayoutCents: parseMoneyToCents(totals.payout as string | number | null),
  }
}

/** FB-S02's rule, applied to the person the money is taken from. */
export function isCharge(payout: PartnerPayout): boolean {
  return payout.payoutCents < 0
}

export function payoutNote(payout: PartnerPayout): string {
  if (!isCharge(payout)) return ''
  return `${tt('Podiel na strate')}: ${formatCents(Math.abs(payout.payoutCents))}`
}

/**
 * `30 % zo zisku 641,40 €` - the share, and what it is a share **of** - §3.68.
 *
 * **This said `30 % z 749,07 €`, naming the revenue**, and it was wrong in both directions: the
 * payout has never been a share of revenue - the owner settled that it is a share of profit - and
 * the server was not sending the figure under that name anyway, so it read `30 % z 0,00 €`.
 *
 * Naming the profit is what makes the three numbers on the card add up: a percentage, the amount it
 * is a percentage of, and the result. A reader can check it.
 */
export function shareNote(payout: PartnerPayout): string {
  return `${formatCount(payout.sharePct)} % ${tt('zo zisku')} ${formatCents(payout.profitCents)}`
}

/**
 * `749,07 € - 107,67 € nákladov` - where the profit came from, said once per card.
 *
 * Without it the card asserts a profit and never shows the revenue it was taken out of, which is the
 * figure a partner recognises from their own invoices.
 */
export function basisNote(payout: PartnerPayout): string {
  if (payout.costCents <= 0) return formatCents(payout.revenueCents)
  return `${formatCents(payout.revenueCents)} - ${formatCents(payout.costCents)} ${tt('nákladov')}`
}

/**
 * Franchises the partner holds a share in but which are switched off.
 *
 * Worth naming: a share of a deactivated franchise earns nothing and looks identical to a share of a
 * quiet one.
 */
export function inactiveFranchises(franchises: readonly PartnerFranchise[]): PartnerFranchise[] {
  return franchises.filter((franchise) => !franchise.isActive)
}

/**
 * §3.122's two row helpers.
 *
 * <p>**The portal rendered no field of `/me/franchises` at all.** It used the array for `inactiveNote` -
 * *how many* are switched off - and for `franchises.length`, so a partner saw a count and never a name.
 * That bites hardest on the case with the least: `ProfitabilityService.report` selects
 * `WHERE is_active = true`, so a **deactivated** franchise gets no payout card either, and the only trace
 * of it was a digit in a sentence.
 *
 * <p>**There is no `partnerHoldings(franchises)` here and that is deliberate.** The first draft had one and
 * it was the identity function - the rows are rendered in the order the server sends them, `ORDER BY f.id`,
 * because sorting the inactive ones to the bottom would be a second opinion about what matters and the row
 * already says which it is. A pass-through helper invites a test that proves nothing.
 */
/** What a row says about the package, where there may not be one. */
export function packageNote(franchise: PartnerFranchise): string {
  return franchise.packageName ?? tt('bez balíka')
}

/** The share on the assignment, which is not the payout's - §3.122. */
export function holdingShareNote(franchise: PartnerFranchise): string {
  return `${formatCount(franchise.sharePct)} %`
}

export function inactiveNote(franchises: readonly PartnerFranchise[]): string {
  const inactive = inactiveFranchises(franchises)
  if (inactive.length === 0) return ''
  return `${formatCount(inactive.length)} ${tt('z vašich franšíz je neaktívnych.')}`
}

/** Said when the partner holds no shares at all, which is not an error. */
export function emptyNote(): string {
  return tt('Zatiaľ nemáte podiel v žiadnej franšíze.')
}

// ─── The invoices behind the payout - §3.40 ──────────────────────────────────

/**
 * What the platform billed the franchises this partner holds a share in.
 *
 * **`GET /partner/me/invoices` had no caller.** The portal read `auth/me`, `me/franchises` and
 * `me/profitability` and not this, while legacy's own `PartnerPortal.tsx` has a full invoices table
 * against the same path. Both of the owner's partners hold shares in all three franchises, so each
 * had **6 invoices worth 3 434,16 EUR** they could not see - a cost against the profit they take a
 * share of, and the documents behind the payout figure the portal already shows them.
 */
export interface PartnerInvoice {
  id: number
  invoiceNumber: string
  franchiseName: string
  /** ISO, or `''` - a draft has no issue date and still belongs on the list. */
  issuedAt: string
  dueAt: string
  paidAt: string
  amountCents: number
  status: string
}

export type InvoiceStanding = 'paid' | 'overdue' | 'open'

function partnerText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

/** `numeric` arrives as a string; cents, because summing euros in floating point drifts. */
function partnerCents(value: unknown): number {
  if (typeof value === 'number') return Math.round(value * 100)
  const parsed = Number(partnerText(value).replace(',', '.'))
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
}

export function partnerInvoicesOf(payload: unknown): PartnerInvoice[] {
  const rows = (payload as { invoices?: unknown } | undefined)?.invoices
  if (!Array.isArray(rows)) return []
  return (rows as Record<string, unknown>[]).flatMap((row) =>
    typeof row.id === 'number'
      ? [
          {
            id: row.id,
            invoiceNumber: partnerText(row.invoiceNumber),
            franchiseName: partnerText(row.franchiseName),
            issuedAt: partnerText(row.issuedAt),
            dueAt: partnerText(row.dueAt),
            paidAt: partnerText(row.paidAt),
            amountCents: partnerCents(row.amount),
            status: partnerText(row.status),
          },
        ]
      : [],
  )
}

/**
 * Paid, late, or neither - computed rather than read off `status`.
 *
 * **Legacy reads `status === 'overdue'` and nothing sets that.** `PlatformInvoiceService.STATUSES`
 * accepts `overdue`, the billing run never writes it, and every one of the owner's six rows says
 * `issued` - while **all six are unpaid and past due**, the oldest since 2026-04-15. So legacy's own
 * screen would tell a partner *issued* about an invoice three months late.
 *
 * The endpoint already sends `dueAt` and legacy's UI ignores it. This is the same answer the Tower
 * gives (§3.8's `isOverdue`), with the stored status kept as the fallback for a row somebody has set
 * by hand.
 */
export function invoiceStandingOf(invoice: PartnerInvoice, now: Date): InvoiceStanding {
  if (invoice.paidAt !== '' || invoice.status === 'paid') return 'paid'
  if (invoice.dueAt !== '') {
    const due = new Date(invoice.dueAt)
    if (!Number.isNaN(due.getTime()) && due.getTime() < now.getTime()) return 'overdue'
  }
  return invoice.status === 'overdue' ? 'overdue' : 'open'
}

export function invoiceStandingLabel(standing: InvoiceStanding, status: string): string {
  if (standing === 'paid') return tt('Zaplatená')
  if (standing === 'overdue') return tt('Po splatnosti')
  // The raw status only where it is neither - `issued`, `draft` or `void`, which say what they mean.
  return status === '' ? tt('Vystavená') : status
}

/**
 * The badge colour for a standing - `SAAS.md §3.100`.
 *
 * **Beside the label, because the two were apart and the colour was the one that drifted.** The
 * column drew the word and chose `text-emerald-500` / `text-red-500` / muted in a nested ternary in
 * the JSX - a state rendered as a coloured word, which §3.96 called the real gap and whose ratchet
 * then looked for a `rounded-full` pill instead and could not see this. One function per state, so a
 * fourth standing cannot arrive with a label and no colour.
 *
 * `red` for late rather than `amber`: this is money the platform is owed and a partner reading their
 * own payout has no way to chase it, so it is a fact rather than a warning.
 */
export function invoiceStandingColor(standing: InvoiceStanding): 'green' | 'red' | 'zinc' {
  if (standing === 'paid') return 'green'
  if (standing === 'overdue') return 'red'
  return 'zinc'
}

/**
 * Newest first, and capped - legacy's own shape.
 *
 * The cap is legacy's 50. A partner in three franchises accumulates a row a month per franchise, so
 * the list is a record rather than a working set; a page of it is enough to see what is outstanding.
 */
export function partnerInvoicesNewestFirst(
  invoices: readonly PartnerInvoice[],
  limit = 50,
): PartnerInvoice[] {
  return [...invoices]
    .sort((left, right) => right.issuedAt.localeCompare(left.issuedAt))
    .slice(0, limit)
}

/** What is still owed across the partner's franchises, which no figure on the page answered. */
export function outstandingCents(invoices: readonly PartnerInvoice[], now: Date): number {
  return invoices
    .filter((invoice) => invoiceStandingOf(invoice, now) !== 'paid')
    .reduce((sum, invoice) => sum + invoice.amountCents, 0)
}
